import { TweenLite } from 'gsap';
import { dispatchCustomEvent, throttle } from '../../tools/helpers';
import { ANIMATIONS, BREAKPOINTS_NUMERIC, KEYCODES, CONFIG } from '../config';

class BfvBildergalerieLightbox {
  constructor(options = {}) {
    this.$root = options.element;
    this.$scrollViewbox = this.$root.querySelector('.js-scroll-viewbox');
    this.$imageList = this.$scrollViewbox.querySelector('.js-image-list');
    this.$images = this.$imageList.querySelectorAll('.js-lightbox-image');
    this.$scrollIndicatorBar = this.$root.querySelector('.js-scroll-indicator-bar');
    this.$scrollIndicatorCounter = this.$root.querySelector('.js-scroll-indicator-counter');
    this.$scrollIndicatorCounterViewbox = this.$root.querySelector('.js-scroll-indicator-counter-viewbox');
    this.$scrollIndicatorCounterCurrent =
      this.$scrollIndicatorCounter.querySelector('.js-scroll-indicator-counter-current');
    this.$scrollIndicatorCounterTotal =
      this.$scrollIndicatorCounter.querySelector('.js-scroll-indicator-counter-total');
    this.$closeButton = this.$root.querySelector('.js-close-button');
    this.$body = document.querySelector('body');
    this.$wrapper = this.$root.closest('.js-lightbox-wrapper');
    this.$teaser = this.$wrapper.querySelector('.js-lightbox-teaser');

    this.timeScroll = 100;
  }

  init() {
    this.initCounterTotal();
    this.initCounterPosition();
    // We need our own lightbox scroll event, since we are on a fixed layer
    this.initEventLightboxScroll();
    this.bindEvents();
  }

  initEventLightboxScroll() {
    this.$scrollViewbox.addEventListener('scroll', throttle(() => {
      this.handleEventLightboxScroll();
    }, this.timeScroll));
  }

  // noinspection JSMethodCanBeStatic
  handleEventLightboxScroll() {
    dispatchCustomEvent(
      CONFIG.events.LIGHTBOX_SCROLL,
    );
  }

  bindEvents() {
    window.addEventListener(CONFIG.events.LIGHTBOX_SCROLL, () => {
      const viewportHeight = this.$scrollViewbox.clientHeight;
      this.scrollIndicatorAnimation(viewportHeight);
      const scrollTop = this.$scrollViewbox.scrollTop;
      this.getCurrentScrollElement(viewportHeight, scrollTop);
    });

    window.addEventListener(CONFIG.events.VIEWPORT_CHANGED, () => {
      const viewportHeight = this.$scrollViewbox.clientHeight;
      this.initCounterPosition();
      this.scrollIndicatorAnimation(viewportHeight);
    });

    document.addEventListener('keydown', (event) => {
      const key = event.keyCode || event.which;
      if (key === KEYCODES.ESCAPE) {
        this.closeLightbox();
      }
    }, true);

    this.$closeButton.addEventListener('click', () => this.closeLightbox());
    this.$teaser.addEventListener('click', () => this.openLightbox());
  }

  openLightbox() {
    this.$root.classList.remove('bfv-gallery-lightbox--closed');
    this.$root.classList.add('bfv-gallery-lightbox--open');
    this.$body.classList.add('no-scroll');
  }

  closeLightbox() {
    this.$root.classList.remove('bfv-gallery-lightbox--open');
    this.$root.classList.add('bfv-gallery-lightbox--closed');
    this.$body.classList.remove('no-scroll');
  }

  /**
   * Set the counter total to count of pictures
   */
  initCounterTotal() {
    this.$scrollIndicatorCounterTotal.innerHTML = this.$images.length;
  }

  /**
   * Set the initial position of the scroll counter
   */
  initCounterPosition() {
    const viewportWidth = this.$scrollIndicatorCounterViewbox.clientWidth;
    const scrollIndicatorCounterWidth = this.$scrollIndicatorCounter.getBoundingClientRect().width;
    const windowWidth = window.innerWidth;

    let scrollIndicatorCounterRightInPercent = 0;
    if (windowWidth <= BREAKPOINTS_NUMERIC.medium) { // viewports xs / s / m
      scrollIndicatorCounterRightInPercent = 100 * (viewportWidth - scrollIndicatorCounterWidth) / 2 / viewportWidth;
    } else { // viewports s / m
      scrollIndicatorCounterRightInPercent = 100 * (viewportWidth - scrollIndicatorCounterWidth) / viewportWidth;
    }

    TweenLite.set(this.$scrollIndicatorCounter, {
      right: `${scrollIndicatorCounterRightInPercent}%`,
    });
  }

  /**
   * The Animation of the scroll bar and scroll counter
   * @param viewportHeight
   */
  scrollIndicatorAnimation(viewportHeight) {
    // scroll bar
    const scrollTop = this.$scrollViewbox.scrollTop;
    const scrollHeight = this.$scrollViewbox.scrollHeight - viewportHeight;
    const scrollIndicatorBarWidthInPercent = scrollTop / scrollHeight * 100; // / delta;

    this.$scrollIndicatorBar.style.width = `${scrollIndicatorBarWidthInPercent}%`;

    TweenLite.to(this.$scrollIndicatorBar, ANIMATIONS.default.HALF_DURATION, {
      ease: 'linear',
      width: `${scrollIndicatorBarWidthInPercent}%`,
    });

    // scroll counter
    const viewportWidth = this.$scrollIndicatorCounterViewbox.clientWidth;
    const scrollIndicatorCounterWidth = this.$scrollIndicatorCounter.getBoundingClientRect().width;
    const windowWidth = window.innerWidth;

    let scrollIndicatorCounterRightInPercent = 100;
    if (windowWidth > BREAKPOINTS_NUMERIC.medium) { // viewports l / xl
      scrollIndicatorCounterRightInPercent =
         ((100 - scrollIndicatorBarWidthInPercent) * (viewportWidth - scrollIndicatorCounterWidth) / viewportWidth);
      TweenLite.to(this.$scrollIndicatorCounter, ANIMATIONS.default.HALF_DURATION, {
        ease: 'linear',
        right: `${scrollIndicatorCounterRightInPercent}%`,
      });
    }
  }

  /**
   * Returns the index of the currently visited image, i.e. the image whose center is closest to viewport center
   * @param viewportHeight
   */
  getCurrentScrollElement(viewportHeight) {
    const viewportTop = this.$scrollViewbox.offsetTop;
    const verticalCenterOfViewport = viewportTop + (viewportHeight / 2);
    const imageListTop = this.$scrollViewbox.scrollTop;

    // find the element whose center is closest to viewport center
    const matchedElement = Array.prototype.slice.call(this.$images).reduce(($best, $current, index) => {

      const currentElementTop = $current.offsetTop;
      const currentElementHeight = $current.clientHeight;
      const verticalCenterOfElement = viewportTop - imageListTop + currentElementTop + (currentElementHeight / 2);
      const distanceToVerticalCenterOfViewport = Math.abs(verticalCenterOfViewport - verticalCenterOfElement);

      if (distanceToVerticalCenterOfViewport < $best.distanceToVerticalCenterOfViewport) {
        return {
          index: index + 1,
          distanceToVerticalCenterOfViewport,
        };
      }
      return $best;

    }, { distanceToVerticalCenterOfViewport: Infinity, index: 1 });

    this.$scrollIndicatorCounterCurrent.innerHTML = matchedElement.index;
  }
}

export default BfvBildergalerieLightbox;



// WEBPACK FOOTER //
// ./src/javascript/main/BfvBildergalerieLightbox/index.js